import { useLayoutEffect, useRef, useState } from 'react';
import { Paperclip, X, Send, Mic, Sparkles, Loader2 } from 'lucide-react';
import { Button, SpeechToText } from '@dreamtree-org/twreact-ui';
import FilePreview from '@/components/FilePreview';
import { shouldSendOnKey, clampComposerHeight, composeDictation, COMPOSER_LINE_HEIGHT } from '@/lib/composerInput';

const ATTACH_ACCEPT = 'image/*,.csv,.json,.txt,.xlsx,.pdf,.docx';

/** Touch is the primary pointer — the right question, unlike a width breakpoint: a 360px
 *  desktop window is not a phone, and a 1024px tablet is. Read once; a device does not
 *  change its pointer kind mid-session. */
const IS_TOUCH = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

/** The pending-attachment chip shown above the input row once a file has finished
 *  uploading (before Send) — reuses FilePreview so what the user sees here matches
 *  exactly what the resulting message bubble will show. */
function PendingAttachment({ attachment, onRemove }) {
  if (!attachment) return null;
  return (
    <div className="px-2 pb-2 flex items-start gap-2">
      <div className="flex-1 min-w-0">
        {attachment.preview && <FilePreview preview={attachment.preview} downloadUrl={attachment.downloadUrl} />}
        <p className="text-xs text-secondary-500 mt-1 truncate">{attachment.filename}</p>
      </div>
      <button type="button" onClick={onRemove} title="Remove attachment" aria-label="Remove attachment" className="p-1 rounded-lg hover:bg-[var(--app-border)]/40">
        <X size={14} />
      </button>
    </div>
  );
}

const ICON_BUTTON = 'p-2 rounded-full text-secondary-500 hover:bg-[var(--app-border)]/40 '
  + 'disabled:opacity-50 shrink-0 transition-colors '
  + 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500';

/** Whether this browser ships built-in speech recognition at all (Firefox and
 *  some mobile WebViews don't — SpeechToText renders null there). */
const BROWSER_STT_SUPPORTED = typeof window !== 'undefined'
  && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

/** Mic button: browser SpeechRecognition where it exists, else the server-side
 *  dictation fallback (whichever the caller configured), else nothing. */
function DictationButton({
  uploading, serverDictation, startDictation, onDictationInterim, onDictationChunk,
}) {
  if (BROWSER_STT_SUPPORTED) {
    return (
      <SpeechToText
        onStart={startDictation}
        onSpeaking={onDictationInterim}
        onSpeechComplete={onDictationChunk}
        renderButton={({ isSupported, isListening, toggle }) => (
          // Firefox ships no SpeechRecognition at all — render nothing there rather
          // than a button that silently fails (the server fallback below covers it).
          isSupported ? (
            <button
              type="button"
              onClick={toggle}
              disabled={uploading}
              aria-pressed={isListening}
              title={isListening ? 'Stop dictation' : 'Dictate a message'}
              aria-label={isListening ? 'Stop dictation' : 'Dictate a message'}
              className={`${ICON_BUTTON} ${isListening ? 'text-error-500 animate-pulse' : ''}`}
            >
              <Mic size={18} />
            </button>
          ) : null
        )}
      />
    );
  }
  if (serverDictation && serverDictation.supported) {
    return (
      <ServerMicButton
        transcribe={serverDictation.transcribe}
        onStart={startDictation}
        onChunk={onDictationChunk}
        disabled={uploading}
      />
    );
  }
  return null;
}

function ServerMicButton({ transcribe, onStart, onChunk, disabled }) {
  const [recording, setRecording] = useState(false);
  const [working, setWorking] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = async () => {
    if (recRef.current) {
      recRef.current.stop(); // onstop below uploads + transcribes
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        recRef.current = null;
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (!blob.size) return;
        setWorking(true);
        try {
          const text = await transcribe(blob);
          if (text) onChunk(text);
        } catch {
          // transcription failed (rate limit, provider down) — stay silent
        } finally {
          setWorking(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      onStart();
    } catch {
      // mic denied or unavailable — nothing to do
    }
  };

  const buttonTitle = recording ? 'Stop and transcribe' : 'Dictate a message';
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled || working}
      aria-pressed={recording}
      title={working ? 'Transcribing…' : buttonTitle}
      aria-label={buttonTitle}
      className={`${ICON_BUTTON} ${recording ? 'text-error-500 animate-pulse' : ''}`}
    >
      <Mic size={18} />
    </button>
  );
}

/**
 * The message input plus attach button / paste-image / pending-attachment chip — shared by
 * the internal chat (ChatPanel.jsx) and the public embed widget (EmbedChat.jsx), so the
 * attach/paste/pending-chip logic exists in exactly one place instead of two near-
 * identical copies. Deliberately decoupled from *how* an attachment gets uploaded:
 * `onUploadAttachment(file)` just needs to return `{fileId, filename, mimeType,
 * preview}` — the internal surface implements it via useAgentChat.js's fetchApi
 * wrapper, the embed widget via a plain fetch()+FormData call, and this component
 * doesn't care which.
 *
 * Upload happens immediately on attach/paste (not deferred to Send) so the user sees a
 * preview chip and can remove a wrong file before committing to send it — send() only
 * ever receives an already-uploaded fileId.
 *
 * MULTI-LINE. This is a textarea that starts exactly one line tall and grows with its
 * content, not an input that switches modes: a mode flag would eventually disagree with what
 * is actually in the box (paste three paragraphs while in "single-line mode" and the two
 * representations have already diverged). Enter sends and Shift+Enter breaks the line — but
 * note a textarea does NOT submit its form on Enter the way the old lone <input> did, so the
 * keydown handler below is load-bearing, not a convenience. The decisions themselves live in
 * lib/composerInput.js because a .jsx component cannot be rendered in this test harness.
 *
 * `menu` is a node, not a variant flag: both surfaces render the same composer and differ
 * only in WHICH overflow actions they offer, so styling stays single-sourced here.
 *
 * DICTATION is speech-to-TEXT into this box — the browser's own Web Speech API, no backend
 * and no key. Not to be confused with the separate text-to-SPEECH that reads replies aloud
 * (lib/useCharacterSpeech.js), whose mute control lives in the `menu`.
 */
export default function Composer({
  sending, disabled, onSend, onUploadAttachment, placeholder, downloadUrlFor, menu,
  allowVoiceInput = true,
  // Server-side dictation fallback ({supported, transcribe: (blob) => Promise<text>}) —
  // used only where the browser has no built-in SpeechRecognition. Supplied by
  // ChatPanel.jsx (authenticated route) and EmbedChat.jsx (key-scoped route).
  serverDictation,
  // Prompt optimization ((text) => Promise<optimizedText>) — rewrites the draft in
  // place via the agent's tool context. Injected per surface like serverDictation
  // so internal and embed stay key-scoped to their own routes.
  optimizePrompt,
  allowOptimize = true,
}) {
  const [input, setInput] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState('');
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  // Dictation extends whatever was already typed rather than replacing it, so the draft at
  // the moment the mic started is kept aside. Refs, not state: recognition fires many times
  // a second and none of it should re-render anything but the textarea's value.
  const dictationBaseRef = useRef('');
  const dictationCommittedRef = useRef('');

  // Runs on every change — including back to '' after a send, which is what collapses the
  // box again. An early return on empty would leave it stuck tall.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto'; // release first, so scrollHeight can report a SMALLER content box
    // Empty is not measured: Chrome reports an empty textarea's scrollHeight as TWO lines,
    // which would leave the composer permanently a line too tall after every send.
    el.style.height = `${clampComposerHeight(input ? el.scrollHeight : 0)}px`;
  }, [input]);

  const handleFile = async (file) => {
    if (!file || !onUploadAttachment) return;
    setUploading(true);
    setUploadError('');
    try {
      const result = await onUploadAttachment(file);
      setAttachment({ ...result, downloadUrl: downloadUrlFor ? downloadUrlFor(result.fileId) : `/api/agent/files/${result.fileId}` });
    } catch (err) {
      setUploadError((err.response && err.response.data && err.response.data.error) || err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const onFileInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = ''; // allow re-selecting the same file afterwards
  };

  const onPaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find((it) => it.type.startsWith('image/'));
    if (!item) return;
    const file = item.getAsFile();
    if (!file) return;
    e.preventDefault();
    handleFile(file);
  };

  const canSend = (input.trim() || attachment) && !sending && !disabled && !uploading;

  // onSpeechComplete delivers each FINAL chunk as recognition settles it, so chunks
  // accumulate; onSpeaking delivers the phrase still being spoken, so each one supersedes
  // the last. Conflating the two makes the text triple as you talk.
  const startDictation = () => {
    dictationBaseRef.current = input;
    dictationCommittedRef.current = '';
  };
  const onDictationChunk = (chunk) => {
    dictationCommittedRef.current = `${dictationCommittedRef.current} ${chunk}`.trim();
    setInput(composeDictation(dictationBaseRef.current, { committed: dictationCommittedRef.current }));
  };
  const onDictationInterim = (interim) => {
    setInput(composeDictation(dictationBaseRef.current, {
      committed: dictationCommittedRef.current, interim,
    }));
  };

  const submit = (e) => {
    e?.preventDefault();
    if (!input.trim() && !attachment) return;
    onSend(input, attachment);
    setInput('');
    setAttachment(null);
  };

  const onKeyDown = (e) => {
    if (!shouldSendOnKey(e, { isTouch: IS_TOUCH })) return;
    e.preventDefault(); // otherwise the newline lands before React clears the value
    if (canSend) submit();
  };

  // Optimization replaces the draft in place — never auto-sends. Fail-soft: on
  // any error the draft is left untouched so the user loses nothing.
  const optimizeDraft = async () => {
    if (!optimizePrompt || optimizing || !input.trim()) return;
    setOptimizing(true);
    setOptimizeError('');
    try {
      const optimized = await optimizePrompt(input);
      if (optimized && optimized.trim()) setInput(optimized.trim());
    } catch {
      setOptimizeError('Could not optimize — your draft is unchanged.');
    } finally {
      setOptimizing(false);
    }
  };

  return (
    // onPaste stays on the FORM, not the textarea: paste-to-attach has to work while focus is
    // in the pending-attachment area too.
    <form onSubmit={submit} onPaste={onPaste} className="px-2.5 py-2.5 border-t border-[var(--app-border)]">
      <PendingAttachment attachment={attachment} onRemove={() => setAttachment(null)} />
      {uploading && <div className="px-2 pb-1 text-xs text-secondary-500">Uploading…</div>}
      {uploadError && <div className="px-2 pb-1 text-xs text-error-600">{uploadError}</div>}
      {optimizeError && <div className="px-2 pb-1 text-xs text-error-600">{optimizeError}</div>}
      {/* One card, two rows: the text gets the full width it needs, and the controls sit
          under it. A single row would leave ~160px for text in a 360px panel — the opposite
          of the point — and a card that gains a row as you type moves the send button out
          from under the cursor. */}
      <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] focus-within:border-primary-500">
        <textarea
          ref={textareaRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || 'Ask the agent…'}
          aria-label="Message"
          className="block w-full resize-none bg-transparent px-4 py-2 text-sm
            text-[var(--app-text)] placeholder-secondary-400 outline-none overflow-y-auto"
          style={{ lineHeight: `${COMPOSER_LINE_HEIGHT}px` }}
        />
        <div className="flex items-center gap-1 px-1.5 pb-1.5">
          {menu}
          {onUploadAttachment && (
            <>
              <input ref={fileInputRef} type="file" accept={ATTACH_ACCEPT} className="hidden" onChange={onFileInputChange} />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Attach a file"
                aria-label="Attach a file"
                className={ICON_BUTTON}
              >
                <Paperclip size={18} />
              </button>
            </>
          )}
          <div className="flex-1" />
          {allowVoiceInput && (
            <DictationButton
              uploading={uploading}
              serverDictation={serverDictation}
              startDictation={startDictation}
              onDictationInterim={onDictationInterim}
              onDictationChunk={onDictationChunk}
            />
          )}
          {allowOptimize && optimizePrompt && (
            <button
              type="button"
              onClick={optimizeDraft}
              disabled={uploading || optimizing || !input.trim()}
              title={optimizing ? 'Optimizing…' : 'Optimize prompt'}
              aria-label={optimizing ? 'Optimizing…' : 'Optimize prompt'}
              className={ICON_BUTTON}
            >
              {optimizing ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            </button>
          )}
          <Button
            type="submit"
            disabled={!canSend}
            aria-label="Send message"
            title="Send message"
            className="w-9 h-9 p-0 rounded-full shrink-0"
          >
            <Send size={15} />
          </Button>
        </div>
      </div>
    </form>
  );
}
